/**
 * Folding state per matter, kept in localStorage so a doctor's view survives
 * a reload (docs/ux/workspace.md). Keys are `section:<key>` and
 * `group:<sectionKey>:<number>`. Storage may be unavailable; folding then
 * lives for the page only.
 */

import { useCallback, useMemo, useState } from 'react';

const storageKey = (ref: string) => `beam.workspace.folded:${ref}`;

function read(ref: string): Set<string> {
  try {
    const raw = window.localStorage.getItem(storageKey(ref));
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
}

function write(ref: string, set: Set<string>): void {
  try {
    window.localStorage.setItem(storageKey(ref), JSON.stringify([...set]));
  } catch {
    // Private mode: the page keeps its own copy.
  }
}

export const sectionFoldKey = (sectionKey: string) => `section:${sectionKey}`;
export const groupFoldKey = (sectionKey: string, number: string) => `group:${sectionKey}:${number}`;

export function useFolding(ref: string, sectionKeys: string[]) {
  const [folded, setFolded] = useState<Set<string>>(() => read(ref));

  const update = useCallback((fn: (next: Set<string>) => void) => {
    setFolded((prev) => {
      const next = new Set(prev);
      fn(next);
      write(ref, next);
      return next;
    });
  }, [ref]);

  const toggle = useCallback((key: string) => update((next) => { if (next.has(key)) next.delete(key); else next.add(key); }), [update]);
  const allFolded = useMemo(() => sectionKeys.length > 0 && sectionKeys.every((k) => folded.has(sectionFoldKey(k))), [folded, sectionKeys]);
  const collapseAll = useCallback(() => update((next) => { for (const k of sectionKeys) next.add(sectionFoldKey(k)); }), [update, sectionKeys]);
  const expandAll = useCallback(() => update((next) => { for (const k of sectionKeys) next.delete(sectionFoldKey(k)); }), [update, sectionKeys]);

  /** Unfold whatever hides a box so stepping to it can show it. */
  const reveal = useCallback((sectionKey: string, questionNumber: string | null) => update((next) => {
    next.delete(sectionFoldKey(sectionKey));
    if (questionNumber) next.delete(groupFoldKey(sectionKey, questionNumber));
  }), [update]);

  return { folded, isFolded: (key: string) => folded.has(key), toggle, allFolded, collapseAll, expandAll, reveal };
}

export type Folding = ReturnType<typeof useFolding>;
